export const NORTH_INDIAN_VIEWBOX = 100;

/** Geometric center of a house polygon - default planet anchor for every bhava. */
export function getPolygonCentroid(polygon) {
  const count = polygon.length;
  if (!count) return { x: 50, y: 50 };
  return {
    x: polygon.reduce((sum, [x]) => sum + x, 0) / count,
    y: polygon.reduce((sum, [, y]) => sum + y, 0) / count,
  };
}

function roundCoord(value) {
  return Math.round(value * 10) / 10;
}

function pointInPolygon(point, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    if ((yi > point.y) !== (yj > point.y) && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function distancePointToSegment(px, py, [x1, y1], [x2, y2]) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (dx === 0 && dy === 0) {
    return Math.hypot(px - x1, py - y1);
  }
  let t = ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy);
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

/** Minimum distance from a point to any edge of the house polygon. */
export function minDistanceToPolygonEdges(point, polygon) {
  let min = Infinity;
  for (let i = 0; i < polygon.length; i++) {
    min = Math.min(min, distancePointToSegment(point.x, point.y, polygon[i], polygon[(i + 1) % polygon.length]));
  }
  return min;
}

/** Default clearance for round planet badges (matches DRISHTI_CHART_PLANET_RADIUS + margin). */
export const NORTH_INDIAN_PLANET_EDGE_CLEARANCE = 6;
/** Minimum centre-to-centre gap for round media badges (r ≈ 4.5) in multi-planet grids. */
const PLANET_BADGE_SPREAD = 10;

/**
 * Place a planet anchor inside a bhava, inset from borders when needed.
 * Starts from the hand-tuned layout anchor, then nudges toward chart center.
 */
export function insetPlanetAnchorFromEdges(
  polygon,
  preferred,
  minEdgeClearance = NORTH_INDIAN_PLANET_EDGE_CLEARANCE,
  bias = { x: 50, y: 50 },
) {
  let point = {
    x: preferred?.x ?? bias.x,
    y: preferred?.y ?? bias.y,
  };

  if (!pointInPolygon(point, polygon)) {
    point = getPolygonCentroid(polygon);
  }

  if (minDistanceToPolygonEdges(point, polygon) >= minEdgeClearance) {
    return { x: roundCoord(point.x), y: roundCoord(point.y) };
  }

  let best = point;
  let bestClearance = minDistanceToPolygonEdges(point, polygon);

  for (let t = 0.05; t <= 1; t += 0.05) {
    const candidate = {
      x: point.x + t * (bias.x - point.x),
      y: point.y + t * (bias.y - point.y),
    };
    if (!pointInPolygon(candidate, polygon)) {
      break;
    }
    const clearance = minDistanceToPolygonEdges(candidate, polygon);
    if (clearance >= minEdgeClearance) {
      return { x: roundCoord(candidate.x), y: roundCoord(candidate.y) };
    }
    if (clearance > bestClearance) {
      best = candidate;
      bestClearance = clearance;
    }
  }

  return { x: roundCoord(best.x), y: roundCoord(best.y) };
}

const NORTH_INDIAN_HOUSE_LAYOUT_RAW = [
  {
    house: 1,
    title: 'Lagna',
    polygon: [
      [50, 0],
      [75, 25],
      [50, 50],
      [25, 25],
    ],
    number: { x: 50, y: 43.5, textAnchor: 'middle' },
    label: { x: 50, y: 22 },
    sign: { x: 50, y: 10.5 },
    planets: { x: 50, y: 31.5 },
  },
  {
    house: 2,
    title: 'Dhana',
    polygon: [
      [0, 0],
      [50, 0],
      [25, 25],
    ],
    number: { x: 25, y: 20.5, textAnchor: 'middle' },
    label: { x: 23, y: 10 },
    sign: { x: 11, y: 6.5 },
    planets: { x: 23, y: 8.5 },
  },
  {
    house: 3,
    title: 'Sahaja',
    polygon: [
      [0, 0],
      [25, 25],
      [0, 50],
    ],
    number: { x: 20, y: 25.5, textAnchor: 'middle' },
    label: { x: 10.5, y: 27 },
    sign: { x: 6.5, y: 16.5 },
    planets: { x: 8.3, y: 25 },
  },
  {
    house: 4,
    title: 'Sukha',
    polygon: [
      [0, 50],
      [25, 25],
      [50, 50],
      [25, 75],
    ],
    number: { x: 42.5, y: 50.5, textAnchor: 'middle' },
    label: { x: 24.5, y: 48.5 },
    sign: { x: 13.5, y: 50 },
    planets: { x: 24.5, y: 58.5 },
  },
  {
    house: 5,
    title: 'Putra',
    polygon: [
      [0, 50],
      [25, 75],
      [0, 100],
    ],
    number: { x: 20, y: 74.5, textAnchor: 'middle' },
    label: { x: 12.5, y: 73.5 },
    sign: { x: 6.5, y: 84.5 },
    planets: { x: 8.3, y: 75 },
  },
  {
    house: 6,
    title: 'Ripu',
    polygon: [
      [0, 100],
      [25, 75],
      [50, 100],
    ],
    number: { x: 25, y: 81.5, textAnchor: 'middle' },
    label: { x: 25, y: 90.5 },
    sign: { x: 25, y: 97 },
    planets: { x: 25, y: 93.5 },
  },
  {
    house: 7,
    title: 'Kalatra',
    polygon: [
      [25, 75],
      [50, 50],
      [75, 75],
      [50, 100],
    ],
    number: { x: 50, y: 58.5, textAnchor: 'middle' },
    label: { x: 50, y: 78.5 },
    sign: { x: 50, y: 94.5 },
    planets: { x: 50, y: 67.5 },
  },
  {
    house: 8,
    title: 'Ayush',
    polygon: [
      [50, 100],
      [75, 75],
      [100, 100],
    ],
    number: { x: 75, y: 81.5, textAnchor: 'middle' },
    label: { x: 75, y: 90.5 },
    sign: { x: 75, y: 97 },
    planets: { x: 75, y: 93.5 },
  },
  {
    house: 9,
    title: 'Dharma',
    polygon: [
      [100, 50],
      [75, 75],
      [100, 100],
    ],
    number: { x: 80, y: 74.5, textAnchor: 'middle' },
    label: { x: 87.5, y: 73.5 },
    sign: { x: 93.5, y: 84.5 },
    planets: { x: 91.7, y: 75 },
  },
  {
    house: 10,
    title: 'Karma',
    polygon: [
      [50, 50],
      [75, 25],
      [100, 50],
      [75, 75],
    ],
    number: { x: 57.5, y: 50.5, textAnchor: 'middle' },
    label: { x: 75.5, y: 48.5 },
    sign: { x: 86.5, y: 50 },
    planets: { x: 75.5, y: 60.5 },
  },
  {
    house: 11,
    title: 'Labha',
    polygon: [
      [75, 25],
      [100, 0],
      [100, 50],
    ],
    number: { x: 80, y: 25.5, textAnchor: 'middle' },
    label: { x: 89.5, y: 27 },
    sign: { x: 93.5, y: 16.5 },
    planets: { x: 91.7, y: 25 },
  },
  {
    house: 12,
    title: 'Vyaya',
    polygon: [
      [50, 0],
      [100, 0],
      [75, 25],
    ],
    number: { x: 75, y: 20.5, textAnchor: 'middle' },
    label: { x: 77, y: 10 },
    sign: { x: 89, y: 6.5 },
    planets: { x: 77, y: 8.5 },
  },
];

/** Hand-tuned planet anchors per bhava (visual center), not raw polygon centroids. */
export const NORTH_INDIAN_HOUSE_LAYOUT = NORTH_INDIAN_HOUSE_LAYOUT_RAW;

/** Planet anchors inset from bhava edges - for charts that show round planet badges. */
export const NORTH_INDIAN_HOUSE_CENTROIDS = Object.fromEntries(
  NORTH_INDIAN_HOUSE_LAYOUT.map((layout) => {
    const preferred = layout.planets ?? getPolygonCentroid(layout.polygon);
    return [layout.house, insetPlanetAnchorFromEdges(layout.polygon, preferred)];
  }),
);

export const NORTH_INDIAN_HOUSE_MAP = Object.fromEntries(
  NORTH_INDIAN_HOUSE_LAYOUT.map((item) => [item.house, item])
);

export const NORTH_INDIAN_CHART_LINES = [
  [[0, 0], [100, 0]],
  [[100, 0], [100, 100]],
  [[100, 100], [0, 100]],
  [[0, 100], [0, 0]],
  [[0, 0], [100, 100]],
  [[100, 0], [0, 100]],
  [[50, 0], [25, 25]],
  [[50, 0], [75, 25]],
  [[0, 50], [25, 25]],
  [[0, 50], [25, 75]],
  [[100, 50], [75, 25]],
  [[100, 50], [75, 75]],
  [[50, 100], [25, 75]],
  [[50, 100], [75, 75]],
];

/** Top-centre cardinal mark - rendered above the chart (outside SVG). */
export const NORTH_INDIAN_EAST_LABEL = {
  hi: 'पूर्व',
  en: 'EAST',
};

export function pointsToPath(points) {
  return points.map(([x, y], index) => `${index === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ') + ' Z';
}

/**
 * @param {{ polygon: number[][]; planets?: { x: number; y: number } }} layout
 * @param {{ x: number; y: number } | undefined | null} override
 */
export function resolveHousePlanetAnchor(layout, override) {
  if (override) return override;
  const anchor = layout?.planets;
  if (anchor && Number.isFinite(anchor.x) && Number.isFinite(anchor.y)) {
    return insetPlanetAnchorFromEdges(layout.polygon, anchor);
  }
  return insetPlanetAnchorFromEdges(layout.polygon, getPolygonCentroid(layout.polygon));
}

export function clampHouseNumber(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 1 || numeric > 12) {
    return null;
  }

  return numeric;
}

function prevHouse(house) {
  return house === 1 ? 12 : house - 1;
}

function nextHouse(house) {
  return house === 12 ? 1 : house + 1;
}

function edgeKey(a, b) {
  const [x1, y1] = a;
  const [x2, y2] = b;
  if (x1 < x2 || (x1 === x2 && y1 < y2)) return `${x1},${y1}|${x2},${y2}`;
  return `${x2},${y2}|${x1},${y1}`;
}

function polygonEdges(polygon) {
  const edges = [];
  for (let i = 0; i < polygon.length; i++) {
    edges.push([polygon[i], polygon[(i + 1) % polygon.length]]);
  }
  return edges;
}

function findSharedEdge(polyA, polyB) {
  const keysB = new Set(polygonEdges(polyB).map(([a, b]) => edgeKey(a, b)));
  for (const [a, b] of polygonEdges(polyA)) {
    if (keysB.has(edgeKey(a, b))) return [a, b];
  }
  return null;
}

function edgeMidpoint([a, b]) {
  return { x: roundCoord((a[0] + b[0]) / 2), y: roundCoord((a[1] + b[1]) / 2) };
}

/** 0° = sign entry (previous house CCW), 30° = sign exit (next house CCW). */
function computeHouseSignAxis(house) {
  const layout = NORTH_INDIAN_HOUSE_MAP[house];
  if (!layout) return { start: { x: 50, y: 50 }, end: { x: 50, y: 50 } };

  const entryEdge = findSharedEdge(layout.polygon, NORTH_INDIAN_HOUSE_MAP[prevHouse(house)].polygon);
  const exitEdge = findSharedEdge(layout.polygon, NORTH_INDIAN_HOUSE_MAP[nextHouse(house)].polygon);
  const fallback = layout.planets ?? getPolygonCentroid(layout.polygon);

  if (!entryEdge || !exitEdge) {
    return { start: fallback, end: fallback };
  }

  return {
    start: edgeMidpoint(entryEdge),
    end: edgeMidpoint(exitEdge),
  };
}

export const NORTH_INDIAN_HOUSE_SIGN_AXIS = Object.fromEntries(
  Array.from({ length: 12 }, (_, index) => [index + 1, computeHouseSignAxis(index + 1)]),
);

/**
 * @param {unknown} planet
 * @returns {number|null} Whole degree 0–29 when present on planet object.
 */
export function getPlanetDegreeInSign(planet) {
  if (planet == null || typeof planet !== 'object') return null;
  const raw = planet.degree;
  if (raw === '' || raw == null) return null;
  const d = Number(raw);
  if (!Number.isFinite(d) || d < 0 || d > 29) return null;
  return d;
}

/**
 * Position along the sign axis for a whole degree (0 = sign start, 29 = sign end).
 * @param {{ start: { x: number; y: number }; end: { x: number; y: number } }} axis
 * @param {number} degree
 */
export function positionOnHouseSignAxis(axis, degree) {
  const t = Math.max(0, Math.min(1, degree / 29));
  const inset = 0.22;
  const tt = inset + t * (1 - 2 * inset);
  return {
    x: roundCoord(axis.start.x + (axis.end.x - axis.start.x) * tt),
    y: roundCoord(axis.start.y + (axis.end.y - axis.start.y) * tt),
  };
}

function getGridPlanetSlots(anchor, count, polygon) {
  const maxSpan = polygon ? minDistanceToPolygonEdges(anchor, polygon) * 1.65 : null;
  const columns = count <= 1 ? 1 : count <= 4 ? 2 : 3;
  const rows = Math.ceil(count / columns);
  let horizontalGap = columns === 1 ? 0 : columns === 2 ? PLANET_BADGE_SPREAD : PLANET_BADGE_SPREAD * 0.88;
  let verticalGap = rows === 1 ? 0 : PLANET_BADGE_SPREAD * 0.92;
  if (maxSpan != null && Number.isFinite(maxSpan)) {
    if (columns > 1) horizontalGap = Math.min(horizontalGap, Math.max(2.8, maxSpan * 1.35));
    if (rows > 1) verticalGap = Math.min(verticalGap, Math.max(2.4, maxSpan * 1.15));
  }
  const startX = anchor.x - ((columns - 1) * horizontalGap) / 2;
  const startY = anchor.y - ((rows - 1) * verticalGap) / 2;

  return Array.from({ length: count }, (_, index) => {
    const column = index % columns;
    const row = Math.floor(index / columns);
    return {
      x: roundCoord(startX + column * horizontalGap),
      y: roundCoord(startY + row * verticalGap),
    };
  });
}

function ensurePlanetSlotInsideHouse(slot, polygon) {
  if (!polygon?.length) return slot;
  const anchor = insetPlanetAnchorFromEdges(polygon, { x: slot.x, y: slot.y });
  return { ...slot, x: anchor.x, y: anchor.y };
}

/**
 * Place planets inside a house: degree-ordered along zodiac flow when degrees exist.
 * @param {number} house 1–12
 * @param {unknown[]} planets
 * @param {{ x: number; y: number }} fallbackAnchor
 * @returns {{ x: number; y: number; planet: unknown; index: number }[]}
 */
export function layoutPlanetsInHouseByDegree(house, planets, fallbackAnchor) {
  if (!planets?.length) return [];

  const layout = NORTH_INDIAN_HOUSE_MAP[house];
  const polygon = layout?.polygon;

  const hasAnyDegree = planets.some((planet) => getPlanetDegreeInSign(planet) != null);
  if (!hasAnyDegree) {
    const slots = getGridPlanetSlots(fallbackAnchor, planets.length, polygon);
    return planets
      .map((planet, index) => ensurePlanetSlotInsideHouse({ ...slots[index], planet, index }, polygon))
      .sort((a, b) => a.index - b.index);
  }

  const axis = NORTH_INDIAN_HOUSE_SIGN_AXIS[house] ?? computeHouseSignAxis(house);

  const sorted = planets
    .map((planet, index) => ({
      planet,
      index,
      degree: getPlanetDegreeInSign(planet),
    }))
    .sort((a, b) => {
      if (a.degree == null && b.degree == null) return a.index - b.index;
      if (a.degree == null) return 1;
      if (b.degree == null) return -1;
      return a.degree - b.degree || a.index - b.index;
    });

  const base = sorted.map(({ planet, index, degree }) => {
    if (degree == null) {
      return { x: fallbackAnchor.x, y: fallbackAnchor.y, planet, index };
    }
    return { ...positionOnHouseSignAxis(axis, degree), planet, index };
  });

  const CLUSTER_DEG = 3;
  let clusterStart = 0;

  for (let i = 1; i <= base.length; i++) {
    const prevDeg = sorted[i - 1]?.degree;
    const nextDeg = sorted[i]?.degree;
    const clusterBreak =
      i === base.length ||
      prevDeg == null ||
      nextDeg == null ||
      Math.abs(nextDeg - prevDeg) > CLUSTER_DEG;

    if (!clusterBreak) continue;

    const start = clusterStart;
    const cluster = base.slice(start, i).filter((_, offset) => sorted[start + offset]?.degree != null);
    if (cluster.length > 1) {
      const cx = cluster.reduce((sum, slot) => sum + slot.x, 0) / cluster.length;
      const cy = cluster.reduce((sum, slot) => sum + slot.y, 0) / cluster.length;
      const grid = getGridPlanetSlots({ x: cx, y: cy }, cluster.length, polygon);
      cluster.forEach((slot, offset) => {
        slot.x = grid[offset].x;
        slot.y = grid[offset].y;
      });
    }
    clusterStart = i;
  }

  return base
    .map((slot) => ensurePlanetSlotInsideHouse(slot, polygon))
    .sort((a, b) => a.index - b.index);
}
